import { useState, useEffect, useMemo } from 'react';
import { getNominationRoundYear } from '../../utils/getNominationRoundYear';
import { devError } from '../../utils/devLog';
import {
  CM_PRIMARY_ID,
  ELECTION_TYPE,
  NOMINATION_STATUS,
  SE_COMMITTEE_ID,
} from '../../utils/constants';

export const useCOIData = (
  selectedCommittee,
  coiDeclarationArray,
  availableCommittees,
  nominationArray,
  onError,
) => {
  const [coiData, setCOIData] = useState([]);
  const [loading, setLoading] = useState(false);

  const getCandidateIdsForCommittee = (committeeId) => {
    if (!nominationArray.length) return [];

    const relevantNominations = nominationArray.filter((nomination) => {
      if (
        nomination.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.SUBMITTED ||
        nomination.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.SHORTLISTED ||
        nomination.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.RECOMMENDED
      ) {
        if (
          (committeeId === CM_PRIMARY_ID &&
            nomination.attributes.electionTypeId ===
              ELECTION_TYPE.CORRESPONDING_MEMBER) ||
          (committeeId === SE_COMMITTEE_ID &&
            nomination.attributes.electionTypeId ===
              ELECTION_TYPE.SPECIAL_ELECTION)
        ) {
          return true;
        }

        return nomination.attributes.sectionalCommitteeId === committeeId;
      }

      return false;
    });

    return [
      ...new Set(relevantNominations.map((nom) => nom.attributes.candidateId)),
    ];
  };

  const createDummyCoiDeclaration = (
    candidateId,
    committeeId,
    currentRound,
    nominationId = null,
  ) => ({
    id: `dummy-${candidateId}`,
    attributes: {
      nominationId,
      candidateId,
      userId: null,
      nominationRound: currentRound,
      coiDetail: '',
      sectionalCommitteeId: committeeId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      coiTypes: {
        data: [],
      },
    },
  });

  const getNominationIdForCandidate = (candidateId, committeeId) => {
    const nomination = nominationArray.find((nom) => {
      if (
        nom.attributes.candidateId === candidateId &&
        (nom.attributes.nominationStatusTypeId ===
          NOMINATION_STATUS.SUBMITTED ||
          nom.attributes.nominationStatusTypeId ===
            NOMINATION_STATUS.SHORTLISTED)
      ) {
        return nom.attributes.sectionalCommitteeId === committeeId;
      }
      return false;
    });

    return nomination ? nomination.id : null;
  };

  const processCoiData = (candidateIds, committeeId, currentRound) => {
    const existingCoiDeclarations = coiDeclarationArray.filter(
      (declaration) =>
        candidateIds.includes(declaration.attributes.candidateId) &&
        declaration.attributes.nominationRound === currentRound &&
        declaration.attributes.sectionalCommitteeId === committeeId,
    );

    const candidatesWithCoi = existingCoiDeclarations.map(
      (decl) => decl.attributes.candidateId,
    );

    const candidatesWithoutCoi = candidateIds.filter(
      (candidateId) => !candidatesWithCoi.includes(candidateId),
    );

    const dummyCoiDeclarations = candidatesWithoutCoi.map((candidateId) => {
      const nominationId = getNominationIdForCandidate(
        candidateId,
        committeeId,
      );
      return createDummyCoiDeclaration(
        candidateId,
        committeeId,
        currentRound,
        nominationId,
      );
    });

    return [...existingCoiDeclarations, ...dummyCoiDeclarations];
  };

  const fetchCOIData = async (committeeId) => {
    setLoading(true);
    try {
      const currentRound = getNominationRoundYear(new Date());

      if (committeeId) {
        const candidateIds = getCandidateIdsForCommittee(committeeId);
        const allCoiDeclarations = processCoiData(
          candidateIds,
          committeeId,
          currentRound,
        );
        setCOIData(allCoiDeclarations);
      } else {
        const allCoiDeclarations = [];

        availableCommittees.forEach((committee) => {
          const candidateIds = getCandidateIdsForCommittee(committee.id);

          if (candidateIds.length > 0) {
            const existingCoiForThisCommittee = coiDeclarationArray.filter(
              (declaration) =>
                candidateIds.includes(declaration.attributes.candidateId) &&
                declaration.attributes.nominationRound === currentRound &&
                declaration.attributes.sectionalCommitteeId === committee.id,
            );

            const candidatesWithCoiForThisCommittee =
              existingCoiForThisCommittee.map(
                (decl) => decl.attributes.candidateId,
              );

            const candidatesWithoutCoiForThisCommittee = candidateIds.filter(
              (candidateId) =>
                !candidatesWithCoiForThisCommittee.includes(candidateId),
            );

            const dummyCoiDeclarationsForThisCommittee =
              candidatesWithoutCoiForThisCommittee.map((candidateId) => {
                const nominationId = getNominationIdForCandidate(
                  candidateId,
                  committee.id,
                );
                return createDummyCoiDeclaration(
                  candidateId,
                  committee.id,
                  currentRound,
                  nominationId,
                );
              });

            const committeeCoiDeclarations = [
              ...existingCoiForThisCommittee,
              ...dummyCoiDeclarationsForThisCommittee,
            ];

            allCoiDeclarations.push(...committeeCoiDeclarations);
          }
        });

        setCOIData(allCoiDeclarations);
      }
    } catch (error) {
      devError('Error fetching COI data:', error);
      onError?.('Failed to load COI report data.', 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      selectedCommittee ||
      (selectedCommittee === null && availableCommittees?.length > 0)
    ) {
      fetchCOIData(selectedCommittee);
    } else {
      setCOIData([]);
    }
  }, [
    selectedCommittee,
    coiDeclarationArray,
    availableCommittees,
    nominationArray,
  ]);

  const coiLookup = useMemo(() => {
    const lookup = {};
    coiData.forEach((coi) => {
      const key = `${coi.attributes.candidateId}-${coi.attributes.userId}`;

      if (
        !lookup[key] ||
        (lookup[key].id.toString().startsWith('dummy-') &&
          !coi.id.toString().startsWith('dummy-'))
      ) {
        lookup[key] = coi;
      }
    });
    return lookup;
  }, [coiData]);

  return { coiData, loading, coiLookup };
};
